<template>
  <div>
    <div class="filter-bar">
      <el-select
        v-model="filters.status"
        placeholder="All Statuses"
        clearable
        @change="loadDevices"
        size="small"
        style="width:160px"
      >
        <el-option v-for="s in statuses" :key="s" :label="s" :value="s" />
      </el-select>
      <el-select
        v-model="filters.frequencyPlan"
        placeholder="All Freq Plans"
        clearable
        @change="loadDevices"
        size="small"
        style="width:180px"
      >
        <el-option v-for="f in frequencyPlans" :key="f" :label="f" :value="f" />
      </el-select>
      <el-select
        v-model="filters.kind"
        placeholder="All Kinds"
        clearable
        @change="loadDevices"
        size="small"
        style="width:150px"
      >
        <el-option label="Bridge" value="Bridge" />
        <el-option label="LoraBleDevice" value="LoraBleDevice" />
      </el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadDevices"
        >Refresh</el-button
      >
      <span class="result-count">{{ devices.length }} devices</span>
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        size="small"
        icon="el-icon-upload2"
        @click="showImportModal = true"
        >Import Excel</el-button
      >
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreateModal"
        >Add Manually</el-button
      >
    </div>

    <el-table
      :data="devices"
      v-loading="loading"
      border
      class="app-table"
      :max-height="tableHeight"
    >
      <el-table-column prop="devEui" label="Dev EUI" width="200" />
      <el-table-column prop="appEui" label="App EUI" width="200" />
      <el-table-column prop="model" label="Model" width="140" />
      <el-table-column prop="kind" label="Kind" width="130">
        <template slot-scope="{ row }">
          <el-tag size="small" type="info">{{ row.kind }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="frequencyPlan" label="Freq Plan" width="130" />
      <el-table-column prop="class" label="Class" width="80" align="center" />
      <el-table-column prop="supplier" label="Supplier" width="130" />
      <el-table-column prop="status" label="Status" width="130">
        <template slot-scope="{ row }">
          <el-tag :type="statusType(row.status)" size="small">{{
            row.status
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="230" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button
            v-if="
              (row.status === 'Commissioned' || row.status === 'Assigned') &&
                $permission('Installation Tool.Devices.Edit')
            "
            size="small"
            type="primary"
            plain
            @click="openReturnModal(row)"
            >Return</el-button
          >
          <el-button
            v-if="
              row.status !== 'Decommissioned' &&
                $permission('Installation Tool.Devices.Edit')
            "
            size="small"
            type="danger"
            plain
            @click="openDecommissionModal(row)"
            >Decommission</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <!-- Manual Add Modal -->
    <el-dialog
      title="Add End Device"
      :visible.sync="showCreateModal"
      width="520px"
      @close="resetCreateForm"
    >
      <el-form
        :model="createForm"
        :rules="createRules"
        ref="createForm"
        label-width="130px"
      >
        <el-form-item label="Dev EUI" prop="devEui">
          <el-input v-model="createForm.devEui" placeholder="16 hex chars" />
        </el-form-item>
        <el-form-item label="App EUI" prop="appEui">
          <el-input v-model="createForm.appEui" placeholder="16 hex chars" />
        </el-form-item>
        <el-form-item label="App Key" prop="appKey">
          <el-input
            v-model="createForm.appKey"
            type="password"
            show-password
            placeholder="32 hex chars — encrypted on save"
          />
        </el-form-item>
        <el-form-item label="Model" prop="model">
          <el-input v-model="createForm.model" />
        </el-form-item>
        <el-form-item label="Supplier" prop="supplier">
          <el-input v-model="createForm.supplier" />
        </el-form-item>
        <el-form-item label="Kind" prop="kind">
          <el-select v-model="createForm.kind" style="width:100%">
            <el-option label="Bridge" value="Bridge" />
            <el-option label="LoraBleDevice" value="LoraBleDevice" />
          </el-select>
        </el-form-item>
        <el-form-item label="Frequency Plan" prop="frequencyPlan">
          <el-select
            v-model="createForm.frequencyPlan"
            placeholder="Select"
            style="width:100%"
          >
            <el-option
              v-for="f in frequencyPlans"
              :key="f"
              :label="f"
              :value="f"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="LoRaWAN Class">
          <el-select
            v-model="createForm.class"
            placeholder="Optional"
            style="width:100%"
            clearable
          >
            <el-option label="Class A" value="A" />
            <el-option label="Class B" value="B" />
            <el-option label="Class C" value="C" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="saving" @click="createDevice"
          >ADD DEVICE</el-button
        >
        <el-button class="cancel-btn" @click="showCreateModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Excel Import Modal (Step 1: Upload) -->
    <el-dialog
      title="Import End Devices from Excel"
      :visible.sync="showImportModal"
      width="620px"
      @close="resetImport"
    >
      <template v-if="importStep === 1">
        <el-alert type="info" :closable="false" style="margin-bottom:16px">
          <template slot="title">
            Upload an .xlsx file to batch import end devices.
            <a
              href="/templates/lora-end-device-import-template.xlsx"
              download
              style="margin-left:8px;font-size:12px"
              >Download sample template</a
            >
          </template>
        </el-alert>
        <el-form :model="importForm" label-width="150px">
          <el-form-item label="Supplier Name" required>
            <el-input
              v-model="importForm.supplierName"
              placeholder="e.g. Lansitec"
            />
          </el-form-item>
          <el-form-item label="Shipping List (.xlsx)" required>
            <el-upload
              ref="xlsxUpload"
              action="#"
              :auto-upload="false"
              :limit="1"
              accept=".xlsx"
              :on-change="onFileChange"
              :file-list="importForm.fileList"
            >
              <el-button size="small" icon="el-icon-upload"
                >Select File</el-button
              >
              <div slot="tip" class="el-upload__tip">
                Only .xlsx files, Lansitec format
              </div>
            </el-upload>
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button
            class="save-btn"
            :loading="previewing"
            @click="previewImport"
            :disabled="!importForm.supplierName || !importForm.file"
            >PREVIEW IMPORT</el-button
          >
          <el-button class="cancel-btn" @click="showImportModal = false"
            >CANCEL</el-button
          >
        </span>
      </template>

      <!-- Step 2: Preview & Confirm -->
      <template v-if="importStep === 2">
        <div class="import-summary">
          <div class="summary-item">
            <span class="summary-num text-success">{{
              importPreview.validCount || 0
            }}</span>
            <span class="summary-label">Devices to import</span>
          </div>
          <div class="summary-item">
            <span class="summary-num text-warning">{{
              importPreview.skippedGatewayRowCount || 0
            }}</span>
            <span class="summary-label">Gateway rows skipped</span>
          </div>
          <div class="summary-item">
            <span class="summary-num text-danger">{{
              importPreview.invalidCount || 0
            }}</span>
            <span class="summary-label">Invalid rows</span>
          </div>
        </div>

        <el-table
          :data="importPreview.rows || []"
          max-height="320"
          border
          size="small"
        >
          <el-table-column
            prop="rowNumber"
            label="#"
            width="50"
            align="center"
          />
          <el-table-column prop="devEui" label="Dev EUI" width="180" />
          <el-table-column prop="appEui" label="App EUI" width="180" />
          <el-table-column
            prop="appKey"
            label="App Key"
            width="180"
            show-overflow-tooltip
          />
          <el-table-column prop="kind" label="Kind" width="120" />
          <el-table-column prop="model" label="Model" width="120" />
          <el-table-column
            prop="class"
            label="Class"
            width="70"
            align="center"
          />
          <el-table-column prop="frequencyPlan" label="Freq Plan" width="120" />
          <el-table-column label="Status" width="80" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="row.isValid" type="success" size="mini">OK</el-tag>
              <el-tag v-else type="danger" size="mini">Error</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Issues" min-width="160" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row.issues && row.issues.length ? row.issues.join('; ') : '' }}
            </template>
          </el-table-column>
        </el-table>

        <span slot="footer">
          <el-button
            class="save-btn"
            :loading="confirming"
            @click="confirmImport"
            :disabled="!importPreview.validCount"
            >IMPORT {{ importPreview.validCount || 0 }} DEVICES</el-button
          >
          <el-button class="cancel-btn" @click="importStep = 1">BACK</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Return to Pool Modal -->
    <el-dialog
      title="Return to Pool"
      :visible.sync="showReturnModal"
      width="420px"
    >
      <p>
        End device
        <strong>{{ actionTarget && actionTarget.devEui }}</strong> will be
        removed from LNS and returned to the pool.
      </p>
      <el-form :model="actionForm" ref="returnForm" label-width="80px">
        <el-form-item
          label="Reason"
          :rules="[{ required: true, message: 'Required', trigger: 'blur' }]"
          prop="reason"
        >
          <el-input type="textarea" :rows="3" v-model="actionForm.reason" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="acting" @click="returnToPool"
          >CONFIRM RETURN</el-button
        >
        <el-button class="cancel-btn" @click="showReturnModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Decommission Modal -->
    <el-dialog
      title="Decommission End Device"
      :visible.sync="showDecommissionModal"
      width="420px"
    >
      <el-alert
        type="error"
        title="This action is permanent."
        show-icon
        :closable="false"
        style="margin-bottom:16px"
      />
      <p>
        End device
        <strong>{{ actionTarget && actionTarget.devEui }}</strong> will be
        permanently decommissioned.
      </p>
      <el-form :model="actionForm" ref="decommissionForm" label-width="80px">
        <el-form-item
          label="Reason"
          :rules="[{ required: true, message: 'Required', trigger: 'blur' }]"
          prop="reason"
        >
          <el-input type="textarea" :rows="3" v-model="actionForm.reason" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="delete-btn" :loading="acting" @click="decommission"
          >CONFIRM DECOMMISSION</el-button
        >
        <el-button class="cancel-btn" @click="showDecommissionModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import loraTableHeight from '@/mixins/loraTableHeight'

const FREQUENCY_PLANS = ['AU915', 'EU868', 'US915', 'AS923', 'CN470']
const STATUSES = ['Unassigned', 'Assigned', 'Commissioned', 'Decommissioned']

const emptyCreate = () => ({
  devEui: '',
  appEui: '',
  appKey: '',
  model: '',
  supplier: '',
  kind: 'Bridge',
  frequencyPlan: '',
  class: ''
})

export default {
  name: 'LoraEndDevicePool',
  mixins: [loraTableHeight],
  data() {
    return {
      devices: [],
      loading: false,
      saving: false,
      acting: false,
      previewing: false,
      confirming: false,
      filters: { status: '', frequencyPlan: '', kind: '' },
      frequencyPlans: FREQUENCY_PLANS,
      statuses: STATUSES,
      showCreateModal: false,
      createForm: emptyCreate(),
      createRules: {
        devEui: [{ required: true, message: 'Required', trigger: 'blur' }],
        appEui: [{ required: true, message: 'Required', trigger: 'blur' }],
        appKey: [{ required: true, message: 'Required', trigger: 'blur' }],
        model: [{ required: true, message: 'Required', trigger: 'blur' }],
        supplier: [{ required: true, message: 'Required', trigger: 'blur' }],
        kind: [{ required: true, message: 'Required', trigger: 'change' }],
        frequencyPlan: [
          { required: true, message: 'Required', trigger: 'change' }
        ]
      },
      showImportModal: false,
      importStep: 1,
      importForm: { supplierName: '', file: null, fileList: [] },
      importPreview: {},
      showReturnModal: false,
      showDecommissionModal: false,
      actionTarget: null,
      actionForm: { reason: '' }
    }
  },
  mounted() {
    this.loadDevices()
  },
  methods: {
    async loadDevices() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.frequencyPlan)
          params.frequencyPlan = this.filters.frequencyPlan
        if (this.filters.kind) params.kind = this.filters.kind
        const res = await LoraApiService.getEndDevices(params)
        this.devices = res.data || []
      } catch {
        this.$message.error('Failed to load end devices')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      this.createForm = emptyCreate()
      this.showCreateModal = true
    },
    resetCreateForm() {
      this.$refs.createForm && this.$refs.createForm.resetFields()
    },
    async createDevice() {
      const valid = await this.$refs.createForm.validate().catch(() => false)
      if (!valid) return
      this.saving = true
      try {
        await LoraApiService.createEndDevice(this.createForm)
        this.$message.success('End device added')
        this.showCreateModal = false
        this.loadDevices()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed'))
      } finally {
        this.saving = false
      }
    },
    onFileChange(file) {
      this.importForm.file = file.raw
      this.importForm.fileList = [file]
    },
    resetImport() {
      this.importStep = 1
      this.importForm = { supplierName: '', file: null, fileList: [] }
      this.importPreview = {}
    },
    async previewImport() {
      if (!this.importForm.supplierName || !this.importForm.file) {
        this.$message.warning('Please fill supplier name and select a file')
        return
      }
      this.previewing = true
      try {
        const fd = new FormData()
        fd.append('file', this.importForm.file)
        fd.append('supplierName', this.importForm.supplierName)
        const res = await LoraApiService.importEndDevicesPreview(fd)
        this.importPreview = res.data
        this.importStep = 2
      } catch (e) {
        this.$message.error(loraError(e, 'Preview failed'))
      } finally {
        this.previewing = false
      }
    },
    async confirmImport() {
      this.confirming = true
      try {
        const res = await LoraApiService.importEndDevicesConfirm({
          rows: (this.importPreview.rows || []).filter(r => r.isValid)
        })
        const count =
          (res.data && res.data.importedCount) || this.importPreview.validCount
        this.$message.success(`${count} end devices imported successfully`)
        this.showImportModal = false
        this.loadDevices()
      } catch (e) {
        this.$message.error(loraError(e, 'Import failed'))
      } finally {
        this.confirming = false
      }
    },
    openReturnModal(dev) {
      this.actionTarget = dev
      this.actionForm = { reason: '' }
      this.showReturnModal = true
    },
    async returnToPool() {
      if (!this.actionForm.reason) {
        this.$message.warning('Please provide a reason')
        return
      }
      this.acting = true
      try {
        await LoraApiService.returnEndDeviceToPool(
          this.actionTarget.id,
          this.actionForm.reason
        )
        this.$message.success('Device returned to pool')
        this.showReturnModal = false
        this.loadDevices()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed'))
      } finally {
        this.acting = false
      }
    },
    openDecommissionModal(dev) {
      this.actionTarget = dev
      this.actionForm = { reason: '' }
      this.showDecommissionModal = true
    },
    async decommission() {
      if (!this.actionForm.reason) {
        this.$message.warning('Please provide a reason')
        return
      }
      this.acting = true
      try {
        await LoraApiService.decommissionEndDevice(
          this.actionTarget.id,
          this.actionForm.reason
        )
        this.$message.success('Device decommissioned')
        this.showDecommissionModal = false
        this.loadDevices()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed'))
      } finally {
        this.acting = false
      }
    },
    statusType(s) {
      return (
        {
          Unassigned: 'info',
          Assigned: 'warning',
          Commissioned: 'success',
          Decommissioned: 'danger'
        }[s] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  .result-count {
    margin-left: auto;
    font-size: 13px;
    color: $font-color;
  }
}

.text-danger {
  color: #f56c6c;
}
.import-summary {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
  .summary-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    .summary-num {
      font-size: 28px;
      font-weight: 700;
    }
    .summary-label {
      font-size: 12px;
      color: $font-color;
      margin-top: 4px;
    }
  }
  .text-success {
    color: #67c23a;
  }
  .text-warning {
    color: #e6a23c;
  }
  .text-danger {
    color: #f56c6c;
  }
}
</style>
